import { useSelector } from 'react-redux';
import { SearchImprovedResults } from './search-improved-results.component';
import styles from '../search.page.module.scss';
import { getLang } from 'shared/store/selectors/lang.selector';
import { useMemo, useState, useCallback } from 'react';
import { SectionComponent } from 'src/components/common/section/section.component';
import { ListItemsNavigator } from 'src/components/common/items-navigator';
import { formatNumber } from 'shared/utils';

const SECTION_TYPE_CONTACTS = 'contacts';
const SECTION_TYPE_WORKSPACE = 'workspace';

const SearchGroups = ({ results, renderByType, options, extendedSearch }) => {
  const lang = useSelector(getLang('SEARCH_SCREEN'));
  const workspaces = useSelector(state => state.workspace).list;
  const selectedWorkspaceId = useSelector(state => state.workspace).selected;

  const {
    contactItems = [],
    knowledgeItems = [],
    labItems = [],
    admissionItems = [],
    knowledgeCategories = [],
    labCategories = [],
    admissionCategories = [],
  } = results || {};

  const allWorkspaceItems = useMemo(
    () => [
      ...knowledgeItems,
      ...labItems,
      ...admissionItems,
      ...knowledgeCategories,
      ...labCategories,
      ...admissionCategories,
    ],
    [
      knowledgeItems,
      labItems,
      admissionItems,
      knowledgeCategories,
      labCategories,
      admissionCategories,
    ],
  );

  // Track user-toggled sections; sections not in this map auto-expand when they have results
  const [userToggledSections, setUserToggledSections] = useState({});

  const handleSectionToggle = useCallback((workspaceId, open) => {
    setUserToggledSections(prev => ({ ...prev, [workspaceId]: open }));
  }, []);

  const { contactItems: usersFromExtendedSearch = [], ...restExtendedSearch } =
    extendedSearch || {};

  const filterExtendedSearch = ({ workspaceId }) => {
    const results = Object.keys(restExtendedSearch).reduce((acc, key) => {
      if (restExtendedSearch[key].length) {
        acc[key] = filterByWorkspace({ list: restExtendedSearch[key], workspaceId });
      }

      return acc;
    }, {});

    const resultsCount = Object.keys(results).reduce((acc, key) => acc + results[key].length, 0);
    return { ...results, resultsCount };
  };

  const filterByWorkspace = ({ list = [], workspaceId }) => {
    return list.filter(knowledge => {
      const { item } = knowledge || {};
      return item.workspaceId === workspaceId;
    });
  };

  // if there is one or no workspace, render simple search
  if (workspaces.length <= 1) {
    const allItems = [...contactItems, ...allWorkspaceItems];
    return (
      <ListItemsNavigator>
        {allItems.map(({ type, item }, index) => {
          const sectionOptions = {
            ...options,
            section: workspaces[0]?.name || '',
            sectionType: type === 'contacts' ? SECTION_TYPE_CONTACTS : SECTION_TYPE_WORKSPACE,
            sectionPosition: 1,
            sectionCollapsed: false,
            position: index + 1,
          };
          return renderByType[type]({ item }, index, sectionOptions);
        })}
        <SearchImprovedResults
          extendedSearch={extendedSearch}
          regularResultsCount={allWorkspaceItems.length}
          renderByType={renderByType}
          sectionContext={{
            section: workspaces[0]?.name || '',
            sectionPosition: 1,
            sectionCollapsed: false,
            positionOffset: allItems.length,
          }}
        />
      </ListItemsNavigator>
    );
  }

  // Calculate total regular results count across all workspaces
  const totalRegularResultsCount = contactItems.length + allWorkspaceItems.length;

  const usersTitle = contactItems.length
    ? lang.SECTION_TITLE.replace('{name}', lang.USERS).replace(
        '{number}',
        formatNumber(contactItems.length + usersFromExtendedSearch.length),
      )
    : lang.SECTION_WITHOUT_EXACT_MATCHES.replace('{name}', lang.USERS).replace(
        '{number}',
        formatNumber(usersFromExtendedSearch.length),
      );

  const hasContactResults = contactItems.length + usersFromExtendedSearch.length > 0;

  // Section position counter: contacts is section 1 if shown
  let sectionPositionCounter = hasContactResults ? 1 : 0;
  const contactsSectionPosition = sectionPositionCounter;

  return (
    <div className={styles.marginTop}>
      {hasContactResults && (
        <SectionComponent title={usersTitle} defaultIsOpen={true}>
          <ListItemsNavigator className={styles.items}>
            {contactItems.map(({ type, item }, index) => {
              const sectionOptions = {
                ...options,
                section: lang.USERS,
                sectionType: SECTION_TYPE_CONTACTS,
                sectionPosition: contactsSectionPosition,
                sectionCollapsed: false,
                position: index + 1,
              };
              return renderByType[type]({ item }, index, sectionOptions);
            })}
            <SearchImprovedResults
              extendedSearch={{
                contactItems: usersFromExtendedSearch,
                resultsCount: usersFromExtendedSearch.length,
              }}
              totalRegularResultsCount={totalRegularResultsCount}
              regularResultsCount={contactItems.length}
              renderByType={renderByType}
              sectionContext={{
                section: lang.USERS,
                sectionType: SECTION_TYPE_CONTACTS,
                sectionPosition: contactsSectionPosition,
                sectionCollapsed: false,
                positionOffset: contactItems.length,
              }}
            />
          </ListItemsNavigator>
        </SectionComponent>
      )}

      {workspaces
        .sort(({ id }) => (id === selectedWorkspaceId ? -1 : 1))
        .map(({ id, name }) => {
          const items = filterByWorkspace({ list: allWorkspaceItems, workspaceId: id });
          const filteredExtendedSearch = filterExtendedSearch({ workspaceId: id });
          const totalCount = items.length + filteredExtendedSearch.resultsCount;

          if (totalCount === 0 && selectedWorkspaceId !== id) return null;

          // Show selected workspace collapsed when it has no results
          const hasResults = totalCount > 0;

          sectionPositionCounter++;
          const sectionPosition = sectionPositionCounter;
          const defaultOpen = hasResults && id === selectedWorkspaceId;
          const isOpen = userToggledSections.hasOwnProperty(id)
            ? userToggledSections[id]
            : defaultOpen;

          const sectionTitle =
            items.length > 0
              ? lang.SECTION_TITLE.replace('{name}', name).replace(
                  '{number}',
                  formatNumber(items.length),
                )
              : lang.SECTION_WITHOUT_EXACT_MATCHES.replace('{name}', name).replace(
                  '{number}',
                  formatNumber(filteredExtendedSearch.resultsCount),
                );

          return (
            <div key={`workspace-${id}`}>
              <SectionComponent
                title={sectionTitle}
                isOpen={isOpen}
                onToggle={open => handleSectionToggle(id, open)}>
                <ListItemsNavigator className={styles.items}>
                  {items.map(({ type, item }, index) => {
                    const sectionOptions = {
                      ...options,
                      section: name,
                      sectionType: SECTION_TYPE_WORKSPACE,
                      sectionPosition,
                      sectionCollapsed: !isOpen,
                      position: index + 1,
                    };
                    return renderByType[type]({ item }, index, sectionOptions);
                  })}
                  <SearchImprovedResults
                    extendedSearch={filteredExtendedSearch}
                    totalRegularResultsCount={totalRegularResultsCount}
                    regularResultsCount={items.length}
                    renderByType={renderByType}
                    sectionContext={{
                      section: name,
                      sectionType: SECTION_TYPE_WORKSPACE,
                      sectionPosition,
                      sectionCollapsed: !isOpen,
                      positionOffset: items.length,
                    }}
                  />
                </ListItemsNavigator>
              </SectionComponent>
            </div>
          );
        })}
    </div>
  );
};

export { SearchGroups };
